import React, {
    useState,
    useMemo,
    useEffect,
    useRef,
    useCallback,
} from "react";
import {
    Button,
    Box,
    MenuItem,
    Select,
    FormControl,
    FormControlLabel,
    Typography,
    Paper,
    TextField,
    Divider,
    Checkbox,
    Chip,
    Switch,
    Collapse,
    CircularProgress,
    Tabs,
    Tab,
} from "@mui/material";
import {
    CheckCircle,
    SwapHoriz,
    InfoOutlined,
    ExpandMore,
    ExpandLess,
} from "@mui/icons-material";
import { ReactGrid } from "@silevis/reactgrid";

const EXCEL_EXPORT_FIELDS = [
    { key: "custom_id", display_name: "Line Id" },
    { key: "name", display_name: "Line Name (Description)" },
    { key: "is_internal", display_name: "Is Internal" },
    { key: "vendor_currency", display_name: "Vendor Currency" },
    { key: "vendor_rate", display_name: "Vendor Rate" },
    { key: "rate_type", display_name: "Rate Type" },
    {
        key: "vendor_client_exchange_rate_buffered",
        display_name: "FX (Buffered)",
    },
    { key: "days", display_name: "Qty (Days)" },
    { key: "quantity", display_name: "Units" },
    { key: "ot_1_5", display_name: "OT 1.5" },
    { key: "ot_2_0", display_name: "OT 2.0" },
    { key: "markup", display_name: "Markup" },
    { key: "adjustment", display_name: "Adjustment" },
    { key: "external_notes", display_name: "External Notes" },
    { key: "internal_notes", display_name: "Internal Notes" },
];

const SECTION_TYPE_OPTIONS = [
    {
        value: "tab",
        label: "By Tab / Sheet",
        caption: "Each selected sheet represents one main section.",
    },
    {
        value: "multi_column",
        label: "Dedicated Column",
        caption: "Section titles live in their own separate column.",
    },
    {
        value: "single_column",
        label: "Shares Column with Line Items",
        caption:
            "Section titles appear in the same column as line item names. Rows with no numeric values are treated as section headers.",
    },
    {
        value: "flat",
        label: "No Sections",
        caption:
            "Flat list — all categories and line items go into a single default section.",
    },
];

const CATEGORY_TYPE_OPTIONS = [
    {
        value: "multi_column",
        label: "Dedicated Column",
        caption: "Category titles live in their own separate column.",
    },
    {
        value: "single_column",
        label: "Shares Column with Line Items",
        caption:
            "Category titles appear in the same column as line item names. Rows with no numeric values are treated as category headers.",
    },
    {
        value: "flat",
        label: "No Categories",
        caption:
            "Flat list — all line items go into a single default category.",
    },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

/** Build a default per-tab config snapshot */
const createDefaultTabConfig = () => ({
    headerRowIndex: 0,
    dataStartRowIndex: 1,
    columnMapping: {},
    sectionHierarchyType: "flat",
    categoryHierarchyType: "flat",
    sectionColumnIndex: "",
    categoryColumnIndex: "",
});

// ─── Component ─────────────────────────────────────────────────────────────────

export default function CustomBidImportWizard({
    previewData,
    initialConfig,
    onPrimarySubmit,
    onSecondarySubmit,
    primarySubmitText = "Run Smart Import",
    secondarySubmitText,
    onCancel,
    isImporting,
    onConfigChange,
    onHighlightGroupDepth,
}) {
    const sheets = previewData?.sheets || [];

    // ── Tab selection ───────────────────────────────────────────────────────
    const [selectedSheets, setSelectedSheets] = useState(
        initialConfig?.sheet_names || sheets,
    );

    // ── Per-tab configuration ───────────────────────────────────────────────
    const [perTabEnabled, setPerTabEnabled] = useState(false);
    const [activeConfigTab, setActiveConfigTab] = useState(
        selectedSheets[0] || sheets[0] || "",
    );
    // Stores per-tab config snapshots: { [sheetName]: { headerRowIndex, dataStartRowIndex, columnMapping, ... } }
    const [sheetConfigs, setSheetConfigs] = useState({});

    // ── Active editing buffer (the form always edits these) ─────────────────
    const [columnMapping, setColumnMapping] = useState(
        initialConfig?.column_mapping || {},
    );
    const [headerRowDraft, setHeaderRowDraft] = useState(
        String(initialConfig?.header_row_index ?? 0),
    );
    const [dataStartRowDraft, setDataStartRowDraft] = useState(
        String(initialConfig?.data_start_row_index ?? 1),
    );
    const [headerRowIndex, setHeaderRowIndex] = useState(
        initialConfig?.header_row_index ?? 0,
    );
    const [dataStartRowIndex, setDataStartRowIndex] = useState(
        initialConfig?.data_start_row_index ?? 1,
    );
    const [sectionHierarchyType, setSectionHierarchyType] = useState("flat");
    const [categoryHierarchyType, setCategoryHierarchyType] = useState("flat");
    const [sectionColumnIndex, setSectionColumnIndex] = useState("");
    const [categoryColumnIndex, setCategoryColumnIndex] = useState("");

    // ── UI states ───────────────────────────────────────────────────────────
    const [gridOpen, setGridOpen] = useState(false);
    const [autoPreviewPending, setAutoPreviewPending] = useState(false);

    // ── Snapshot: capture current form state as a per-tab config object ─────
    const captureCurrentConfig = useCallback(
        () => ({
            headerRowIndex,
            dataStartRowIndex,
            columnMapping: { ...columnMapping },
            sectionHierarchyType,
            categoryHierarchyType,
            sectionColumnIndex,
            categoryColumnIndex,
        }),
        [
            headerRowIndex,
            dataStartRowIndex,
            columnMapping,
            sectionHierarchyType,
            categoryHierarchyType,
            sectionColumnIndex,
            categoryColumnIndex,
        ],
    );

    // ── Load: push a snapshot into the form state ───────────────────────────
    const loadTabConfig = useCallback((cfg) => {
        setHeaderRowIndex(cfg.headerRowIndex ?? 0);
        setHeaderRowDraft(String(cfg.headerRowIndex ?? 0));
        setDataStartRowIndex(cfg.dataStartRowIndex ?? 1);
        setDataStartRowDraft(String(cfg.dataStartRowIndex ?? 1));
        setColumnMapping(cfg.columnMapping || {});
        setSectionHierarchyType(cfg.sectionHierarchyType || "flat");
        setCategoryHierarchyType(cfg.categoryHierarchyType || "flat");
        setSectionColumnIndex(cfg.sectionColumnIndex ?? "");
        setCategoryColumnIndex(cfg.categoryColumnIndex ?? "");
    }, []);

    // ── Sync from initialConfig ─────────────────────────────────────────────
    useEffect(() => {
        if (!initialConfig) return;

        if (initialConfig.sheet_names)
            setSelectedSheets(initialConfig.sheet_names);
        if (initialConfig.header_row_index !== undefined) {
            setHeaderRowIndex(initialConfig.header_row_index);
            setHeaderRowDraft(String(initialConfig.header_row_index));
        }
        if (initialConfig.data_start_row_index !== undefined) {
            setDataStartRowIndex(initialConfig.data_start_row_index);
            setDataStartRowDraft(String(initialConfig.data_start_row_index));
        }
        if (initialConfig.column_mapping)
            setColumnMapping(initialConfig.column_mapping);

        const h = initialConfig.hierarchy;
        if (!h) return;

        if (h.section_type) setSectionHierarchyType(h.section_type);
        else if (initialConfig.tab_names_as_sections)
            setSectionHierarchyType("tab");
        else if (h.type === "multi_column")
            setSectionHierarchyType("multi_column");
        else setSectionHierarchyType("flat");

        if (h.category_type) setCategoryHierarchyType(h.category_type);
        else if (h.type === "single_column")
            setCategoryHierarchyType("single_column");
        else if (h.type === "multi_column")
            setCategoryHierarchyType("multi_column");
        else setCategoryHierarchyType("flat");

        const secIdx =
            h.section_column_index != null
                ? h.section_column_index
                : (h.multi_column_rule?.section_column_index ?? "");
        const catIdx =
            h.category_column_index != null
                ? h.category_column_index
                : (h.multi_column_rule?.category_column_index ?? "");
        setSectionColumnIndex(secIdx);
        setCategoryColumnIndex(catIdx);

        // Hydrate per_sheet_configs if present
        if (initialConfig.per_sheet_configs) {
            setPerTabEnabled(true);
            setSheetConfigs(initialConfig.per_sheet_configs);
        }
    }, [initialConfig]);

    // ── Per-tab toggle ──────────────────────────────────────────────────────
    const handleTogglePerTab = (checked) => {
        if (checked) {
            // Turning ON: seed every selected sheet with the current shared config
            const current = captureCurrentConfig();
            const seeded = {};
            selectedSheets.forEach((s) => {
                seeded[s] = { ...current };
            });
            setSheetConfigs(seeded);
            setPerTabEnabled(true);
            setActiveConfigTab(selectedSheets[0] || "");
        } else {
            // Turning OFF: keep whatever is currently in the form as the shared config
            setPerTabEnabled(false);
            setSheetConfigs({});
        }
    };

    // ── Tab switching (save current → load next) ────────────────────────────
    const handleConfigTabChange = (newTab) => {
        if (!perTabEnabled || newTab === activeConfigTab) return;
        // Save current form to the old tab
        setSheetConfigs((prev) => ({
            ...prev,
            [activeConfigTab]: captureCurrentConfig(),
        }));
        // Load the new tab's config (or fall back to defaults)
        const next = sheetConfigs[newTab] || createDefaultTabConfig();
        loadTabConfig(next);
        setActiveConfigTab(newTab);
    };

    // ── Column mapping ──────────────────────────────────────────────────────
    const handleMapChange = (colIndex, fieldKey) => {
        setColumnMapping((prev) => {
            const next = { ...prev };
            if (!fieldKey) delete next[colIndex];
            else next[colIndex] = fieldKey;
            return next;
        });
    };

    // ── Text field blur commit ──────────────────────────────────────────────
    const commitHeaderRow = () => {
        const val = parseInt(headerRowDraft);
        setHeaderRowIndex(isNaN(val) ? 0 : val);
    };
    const commitDataStartRow = () => {
        const val = parseInt(dataStartRowDraft);
        setDataStartRowIndex(isNaN(val) ? 0 : val);
    };

    // ── Build hierarchy sub-config for a given tab config ───────────────────
    const buildHierarchyForTabConfig = useCallback((cfg) => {
        const numericGuardCols = Object.keys(cfg.columnMapping)
            .filter((k) =>
                ["quantity", "vendor_rate", "markup", "days"].includes(
                    cfg.columnMapping[k],
                ),
            )
            .map(Number);
        return {
            section_type: cfg.sectionHierarchyType,
            category_type: cfg.categoryHierarchyType,
            section_column_index:
                cfg.sectionHierarchyType === "multi_column"
                    ? cfg.sectionColumnIndex
                    : null,
            category_column_index:
                cfg.categoryHierarchyType === "multi_column"
                    ? cfg.categoryColumnIndex
                    : null,
            empty_columns_required: numericGuardCols,
            default_section_name: "Imported Section",
            default_category_name: "Imported Category",
        };
    }, []);

    // ── Config builder ──────────────────────────────────────────────────────
    const buildConfig = useCallback(() => {
        const numericGuardCols = Object.keys(columnMapping)
            .filter((k) =>
                ["quantity", "vendor_rate", "markup", "days"].includes(
                    columnMapping[k],
                ),
            )
            .map(Number);

        const base = {
            sheet_names: selectedSheets,
            tab_names_as_sections: sectionHierarchyType === "tab",
            header_row_index: headerRowIndex,
            data_start_row_index: dataStartRowIndex,
            column_mapping: columnMapping,
            hierarchy: {
                section_type: sectionHierarchyType,
                category_type: categoryHierarchyType,
                section_column_index:
                    sectionHierarchyType === "multi_column"
                        ? sectionColumnIndex
                        : null,
                category_column_index:
                    categoryHierarchyType === "multi_column"
                        ? categoryColumnIndex
                        : null,
                empty_columns_required: numericGuardCols,
                default_section_name: "Imported Section",
                default_category_name: "Imported Category",
            },
        };

        if (perTabEnabled) {
            // Flush current form into sheetConfigs for the active tab before emitting
            const allConfigs = {
                ...sheetConfigs,
                [activeConfigTab]: captureCurrentConfig(),
            };
            const perSheetConfigs = {};
            selectedSheets.forEach((sheetName) => {
                const cfg = allConfigs[sheetName];
                if (cfg) {
                    perSheetConfigs[sheetName] = {
                        header_row_index: cfg.headerRowIndex,
                        data_start_row_index: cfg.dataStartRowIndex,
                        column_mapping: cfg.columnMapping,
                        hierarchy: buildHierarchyForTabConfig(cfg),
                    };
                }
            });
            base.per_sheet_configs = perSheetConfigs;
        }

        return base;
    }, [
        selectedSheets,
        headerRowIndex,
        dataStartRowIndex,
        columnMapping,
        sectionHierarchyType,
        categoryHierarchyType,
        sectionColumnIndex,
        categoryColumnIndex,
        perTabEnabled,
        sheetConfigs,
        activeConfigTab,
        captureCurrentConfig,
        buildHierarchyForTabConfig,
    ]);

    // ── Auto-preview: debounced on any config change ────────────────────────
    const autoPreviewTimerRef = useRef(null);
    const isFirstRender = useRef(true);
    const lastConfigSnapshotRef = useRef(null);
    const onConfigChangeRef = useRef(onConfigChange);
    useEffect(() => {
        onConfigChangeRef.current = onConfigChange;
    }, [onConfigChange]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const config = buildConfig();
        const snapshot = JSON.stringify(config);
        if (snapshot === lastConfigSnapshotRef.current) return;

        setAutoPreviewPending(true);
        clearTimeout(autoPreviewTimerRef.current);
        autoPreviewTimerRef.current = setTimeout(() => {
            setAutoPreviewPending(false);
            lastConfigSnapshotRef.current = snapshot;
            onConfigChangeRef.current?.(config);
        }, 400);
        return () => clearTimeout(autoPreviewTimerRef.current);
    }, [buildConfig]);

    // ── ReactGrid helpers ───────────────────────────────────────────────────
    const activePreviewSheet = perTabEnabled
        ? activeConfigTab
        : selectedSheets[0] || sheets[0] || "";
    const sheetData = previewData?.preview_data?.[activePreviewSheet] || [];
    const maxCols = useMemo(
        () =>
            sheetData.length ? Math.max(...sheetData.map((r) => r.length)) : 0,
        [sheetData],
    );

    const getColumns = () => {
        const cols = [{ columnId: "row_label", width: 100 }];
        for (let i = 0; i < maxCols; i++)
            cols.push({ columnId: i.toString(), width: 150 });
        return cols;
    };

    const getRows = () => {
        const rows = [];
        const mappingCells = [{ type: "header", text: "Map Fields ->" }];
        for (let col = 0; col < maxCols; col++) {
            mappingCells.push({
                type: "header",
                text: columnMapping[col] || "Unmapped",
            });
        }
        rows.push({ rowId: "mapping_header", cells: mappingCells });

        sheetData.forEach((r, idx) => {
            let rowLabel = `Row ${idx}`;
            if (idx === headerRowIndex) rowLabel = "HEADERS ►";
            if (idx === dataStartRowIndex) rowLabel = "DATA START ►";
            const cells = [{ type: "header", text: rowLabel }];
            for (let c = 0; c < maxCols; c++) {
                cells.push({
                    type: "text",
                    text: r[c] ? r[c].toString() : "",
                    nonEditable: true,
                });
            }
            rows.push({ rowId: idx, cells });
        });
        return rows;
    };

    const columnPickerForIndex = (value, onChange) => (
        <FormControl
            size="small"
            fullWidth
            onClick={(e) => e.stopPropagation()}
        >
            <Select
                sx={{ marginTop: 1 }}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <MenuItem value="">
                    <em>None</em>
                </MenuItem>
                {Array.from({ length: maxCols }).map((_, c) => (
                    <MenuItem key={c} value={c}>
                        Col {c}
                        {sheetData[headerRowIndex]?.[c]
                            ? ` [${sheetData[headerRowIndex][c]}]`
                            : ""}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const isMissingRequired = !Object.values(columnMapping).includes("name");

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                bgcolor: "background.default",
                color: "text.primary",
                minHeight: "100vh",
                p: 4,
                maxWidth: 800,
                mx: "auto",
            }}
        >
            {/* ──── 1. TAB SELECTION ──── */}
            <Typography
                variant="subtitle1"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
                <InfoOutlined fontSize="small" /> Select tabs to import
            </Typography>
            {sheets.map((sheet) => (
                <Paper
                    key={sheet}
                    onClick={() =>
                        setSelectedSheets((prev) =>
                            prev.includes(sheet)
                                ? prev.filter((s) => s !== sheet)
                                : [...prev, sheet],
                        )
                    }
                    sx={{
                        p: 2,
                        mb: 1.5,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid",
                        borderColor: selectedSheets.includes(sheet)
                            ? "primary.main"
                            : "#333",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Checkbox
                            checked={selectedSheets.includes(sheet)}
                            size="small"
                        />
                        <Box>
                            <Typography variant="body1" fontWeight="bold">
                                {sheet}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Rows detected for import
                            </Typography>
                        </Box>
                    </Box>
                    {selectedSheets.includes(sheet) && (
                        <CheckCircle color="primary" fontSize="small" />
                    )}
                </Paper>
            ))}

            {/* Per-tab config toggle */}
            {selectedSheets.length > 1 && (
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                        <Switch
                            size="small"
                            checked={perTabEnabled}
                            onChange={(e) =>
                                handleTogglePerTab(e.target.checked)
                            }
                        />
                    }
                    label={
                        <Typography variant="caption" color="text.secondary">
                            Configure each tab independently
                        </Typography>
                    }
                />
            )}

            {/* Per-tab config tab bar */}
            {perTabEnabled && selectedSheets.length > 1 && (
                <Paper
                    variant="outlined"
                    sx={{
                        mt: 2,
                        position: "sticky",
                        top: 3,
                        zIndex: 10,
                        bgcolor: "background.paper",
                        borderColor: "#333",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 2, pt: 1, display: "block" }}
                    >
                        Configuring:
                    </Typography>
                    <Tabs
                        value={
                            selectedSheets.indexOf(activeConfigTab) >= 0
                                ? activeConfigTab
                                : selectedSheets[0]
                        }
                        onChange={(_, val) => handleConfigTabChange(val)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 36,
                            "& .MuiTab-root": {
                                minHeight: 36,
                                py: 0.5,
                                textTransform: "none",
                            },
                        }}
                    >
                        {selectedSheets.map((s) => (
                            <Tab key={s} label={s} value={s} />
                        ))}
                    </Tabs>
                </Paper>
            )}

            <Divider sx={{ my: 4, borderColor: "#333" }} />

            {/* ──── 2. BOUNDARY ──── */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                {(perTabEnabled ? activeConfigTab : activePreviewSheet) ||
                    "Sheet Configuration"}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 3, display: "block" }}
            >
                Tab Settings
            </Typography>

            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <SwapHoriz fontSize="small" /> Where does the data start?
                </Typography>
                <TextField
                    fullWidth
                    label="Header Row"
                    size="small"
                    sx={{ mb: 3 }}
                    value={headerRowDraft}
                    onChange={(e) => setHeaderRowDraft(e.target.value)}
                    onBlur={commitHeaderRow}
                    type="number"
                />
                <TextField
                    fullWidth
                    label="Data Start Row"
                    size="small"
                    value={dataStartRowDraft}
                    onChange={(e) => setDataStartRowDraft(e.target.value)}
                    onBlur={commitDataStartRow}
                    type="number"
                />
            </Box>

            {/* ──── 3. SECTION HIERARCHY ──── */}
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <InfoOutlined
                        fontSize="small"
                        onMouseEnter={() => onHighlightGroupDepth(0)}
                        onMouseLeave={() => onHighlightGroupDepth(null)}
                    />
                    How are <strong>&nbsp;sections&nbsp;</strong> defined?
                </Typography>
                {SECTION_TYPE_OPTIONS.map((opt) => (
                    <Paper
                        key={opt.value}
                        onClick={() => setSectionHierarchyType(opt.value)}
                        sx={{
                            p: 2,
                            mb: 1,
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor:
                                sectionHierarchyType === opt.value
                                    ? "primary.main"
                                    : "#333",
                            bgcolor:
                                sectionHierarchyType === opt.value
                                    ? "rgba(76, 175, 80, 0.05)"
                                    : "transparent",
                        }}
                    >
                        <Typography variant="body2" fontWeight="bold">
                            {opt.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {opt.caption}
                        </Typography>
                        {opt.value === "multi_column" &&
                            sectionHierarchyType === "multi_column" &&
                            columnPickerForIndex(
                                sectionColumnIndex,
                                setSectionColumnIndex,
                            )}
                    </Paper>
                ))}
            </Box>

            {/* ──── 4. CATEGORY HIERARCHY ──── */}
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <InfoOutlined
                        fontSize="small"
                        onMouseEnter={() => onHighlightGroupDepth(1)}
                        onMouseLeave={() => onHighlightGroupDepth(null)}
                    />
                    How are <strong>&nbsp;categories&nbsp;</strong> defined?
                </Typography>
                {CATEGORY_TYPE_OPTIONS.map((opt) => (
                    <Paper
                        key={opt.value}
                        onClick={() => setCategoryHierarchyType(opt.value)}
                        sx={{
                            p: 2,
                            mb: 1,
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor:
                                categoryHierarchyType === opt.value
                                    ? "primary.main"
                                    : "#333",
                            bgcolor:
                                categoryHierarchyType === opt.value
                                    ? "rgba(76, 175, 80, 0.05)"
                                    : "transparent",
                        }}
                    >
                        <Typography variant="body2" fontWeight="bold">
                            {opt.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {opt.caption}
                        </Typography>
                        {opt.value === "multi_column" &&
                            categoryHierarchyType === "multi_column" &&
                            columnPickerForIndex(
                                categoryColumnIndex,
                                setCategoryColumnIndex,
                            )}
                    </Paper>
                ))}
            </Box>

            {/* ──── 5. COLUMN MAPPING ──── */}
            <Typography
                variant="body2"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
                <InfoOutlined fontSize="small" /> Map Columns
            </Typography>
            {Array.from({ length: maxCols }).map((_, c) => {
                const rawHeader =
                    sheetData[headerRowIndex]?.[c] || `Column ${c}`;
                return (
                    <Box
                        key={c}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >
                        <Box sx={{ width: 150 }}>
                            <Typography variant="body2" color="text.secondary">
                                {rawHeader}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                flexGrow: 1,
                            }}
                        >
                            <SwapHoriz
                                sx={{ color: "text.secondary", fontSize: 18 }}
                            />
                            <FormControl
                                size="small"
                                sx={{ minWidth: 200, flexGrow: 1 }}
                            >
                                <Select
                                    value={columnMapping[c] || ""}
                                    onChange={(e) =>
                                        handleMapChange(c, e.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        <em>Do Not Import</em>
                                    </MenuItem>
                                    {EXCEL_EXPORT_FIELDS.map((f) => (
                                        <MenuItem key={f.key} value={f.key}>
                                            {f.display_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                );
            })}

            {/* ──── 6. DATA PREVIEW GRID — collapsible ──── */}
            <Box sx={{ mb: 4 }}>
                <Box
                    onClick={() => setGridOpen((prev) => !prev)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        py: 1,
                        borderBottom: "1px solid #333",
                        "&:hover": { opacity: 0.8 },
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <InfoOutlined fontSize="small" />
                        Raw Data Preview
                        {autoPreviewPending && (
                            <CircularProgress size={12} sx={{ ml: 1 }} />
                        )}
                    </Typography>
                    {gridOpen ? (
                        <ExpandLess fontSize="small" />
                    ) : (
                        <ExpandMore fontSize="small" />
                    )}
                </Box>

                <Collapse in={gridOpen}>
                    <Box
                        sx={{
                            mt: 1,
                            minHeight: 0,
                            overflow: "auto",
                            border: "1px solid #333",
                            borderRadius: 1,
                            "& .reactgrid-content .rg-pane .rg-cell": {
                                color: "text.primary",
                                backgroundColor: "background.paper",
                            },
                            "& .reactgrid-content .rg-pane .rg-cell.rg-header-cell":
                                {
                                    color: "text.primary",
                                    backgroundColor: "background.default",
                                },
                        }}
                    >
                        <ReactGrid
                            rows={getRows()}
                            columns={getColumns()}
                            enableRowSelection
                            onActiveCellChange={(cell) => {
                                if (typeof cell.rowId === "number") {
                                    if (
                                        window.confirm(
                                            `Set row ${cell.rowId} as the Header Row and row ${cell.rowId + 1} as Data Start row?`,
                                        )
                                    ) {
                                        const newHeader = cell.rowId;
                                        const newStart = cell.rowId + 1;
                                        setHeaderRowIndex(newHeader);
                                        setHeaderRowDraft(String(newHeader));
                                        setDataStartRowIndex(newStart);
                                        setDataStartRowDraft(String(newStart));
                                    }
                                }
                            }}
                        />
                    </Box>
                </Collapse>
            </Box>

            {/* ──── FOOTER ──── */}
            <Box
                sx={{
                    mt: 6,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                }}
            >
                <Button onClick={onCancel} sx={{ color: "text.secondary" }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isImporting || isMissingRequired}
                    onClick={() => onPrimarySubmit(buildConfig())}
                    sx={{ fontWeight: "bold", px: 4 }}
                >
                    {isImporting ? "Processing..." : primarySubmitText}
                </Button>
            </Box>
            {isMissingRequired && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block", textAlign: "right" }}
                >
                    * Description (Line Name) mapping is required.
                </Typography>
            )}
        </Box>
    );
}
